import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getFailoverAwareAuth, getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { doc, getDoc, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, FirebaseUserType } from '../services/firebaseImports';
import { serverTimestamp as firestoreServerTimestamp } from 'firebase/firestore';
import { getDatabase, ref, onDisconnect, set, serverTimestamp } from 'firebase/database';
import { MigrationDataAccessLayer } from '../services/migrationDataAccessLayer';
import { locationService } from '../services/locationService';
import Logger from '../services/logger';
import { FIRESTORE_COLLECTIONS } from '../constants/firestoreCollections';

interface AuthContextType {
  user: FirebaseUserType | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  register: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUserType | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const locationTrackingCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Skip Firebase during pre-render/SSR
    // Check if we're in SSR environment (Node.js) or headless browser
    const isPreRender = typeof window === 'undefined' || typeof navigator === 'undefined' || (
      /HeadlessChrome/i.test(navigator?.userAgent || '')
    );
    
    if (isPreRender) {
      setIsLoading(false);
      return;
    }

    const auth = getFailoverAwareAuth();
    const db = getFailoverAwareDatabase();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      Logger.debug('[AuthContext] onAuthStateChanged triggered with user:', { uid: firebaseUser ? firebaseUser.uid : 'null' });
      
      // FIX: Ensure user.uid is always the Firebase UID, not email
      // If firebaseUser.uid is an email (bug), use auth.currentUser.uid instead
      if (firebaseUser) {
        const currentAuthUser = auth.currentUser;
        
        // If firebaseUser.uid is an email, use the current auth user's uid
        if (firebaseUser.uid && firebaseUser.uid.includes('@')) {
          Logger.warn('[AuthContext] FIXING: firebaseUser.uid is an email, using auth.currentUser.uid instead');
          if (currentAuthUser && currentAuthUser.uid && !currentAuthUser.uid.includes('@')) {
            // Use the current auth user which has the correct uid
            setUser(currentAuthUser);
          } else {
            // Fallback: still set the firebaseUser but log the issue
            Logger.error('[AuthContext] ERROR: Both firebaseUser.uid and currentAuthUser.uid are emails!', new Error('Invalid UID format'));
            setUser(firebaseUser);
          }
        } else {
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
      }
      setIsAuthenticated(!!firebaseUser);
      setIsLoading(false);
      if (firebaseUser) {
        // Firestore status using companyUsers collection
        // Only update status if document exists with actual profile data (prevents creating empty documents)
        try {
          const companyUserDoc = doc(db, FIRESTORE_COLLECTIONS.COMPANY_USERS, firebaseUser.uid);
          const docSnapshot = await getDoc(companyUserDoc);
          
          // Only update status if document exists AND has profile data (not just status field)
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();
            const hasProfileData = docData?.companyName || docData?.email || docData?.displayName;
            
            if (hasProfileData) {
              const migrationDataAccess = new MigrationDataAccessLayer();
              await migrationDataAccess.updateUserProfile(firebaseUser.uid, {
                status: 'online',
                lastActive: firestoreServerTimestamp()
              });
            } else {
              // Document exists but only has status/metadata - don't update, might be empty doc
              Logger.warn('[AuthContext] Document exists but has no profile data, skipping status update');
            }
          } else {
            // Document doesn't exist - don't create empty document
            Logger.debug('[AuthContext] User document does not exist in companyUsers, skipping status update');
          }
        } catch (err) {
          Logger.error('Failed to set user online status:', err as Error);
        }
        // Realtime Database presence (with error handling)
        try {
          const dbRtdb = getDatabase();
          const userStatusDatabaseRef = ref(dbRtdb, '/status/' + firebaseUser.uid);
          set(userStatusDatabaseRef, {
            state: 'online',
            last_changed: serverTimestamp(),
          });
          onDisconnect(userStatusDatabaseRef).set({
            state: 'offline',
            last_changed: serverTimestamp(),
          });
        } catch (rtdbError) {
          Logger.warn('Failed to set Realtime Database status (non-critical):', rtdbError);
          // Don't throw error - this is not critical for the main functionality
        }

        // Start location tracking for all authenticated users (carrier, shipper, broker)
        // This ensures continuous location tracking across all pages
        try {
          // Clean up any existing tracking first
          if (locationTrackingCleanupRef.current) {
            locationTrackingCleanupRef.current();
            locationTrackingCleanupRef.current = null;
          }

          // Check if geolocation is available before starting tracking
          if (typeof navigator !== 'undefined' && navigator.geolocation) {
            Logger.debug('[AuthContext] Starting location tracking for user:', { uid: firebaseUser.uid });
            const cleanup = locationService.startTracking(firebaseUser.uid);
            locationTrackingCleanupRef.current = cleanup;
          } else {
            Logger.warn('[AuthContext] Geolocation not available, skipping location tracking');
          }
        } catch (locationError) {
          Logger.error('[AuthContext] Failed to start location tracking:', locationError as Error);
          // Don't block authentication if location tracking fails
        }
      } else {
        // User logged out - stop location tracking
        if (locationTrackingCleanupRef.current) {
          Logger.debug('[AuthContext] Stopping location tracking');
          locationTrackingCleanupRef.current();
          locationTrackingCleanupRef.current = null;
        }
        
        // Also mark as offline in location service
        if (user?.uid) {
          locationService.stopTracking(user.uid).catch((err) => {
            Logger.warn('[AuthContext] Failed to stop location tracking:', err);
          });
        }
      }
    });
    return () => {
      unsubscribe();
      // Cleanup location tracking on unmount
      if (locationTrackingCleanupRef.current) {
        locationTrackingCleanupRef.current();
      }
    };
  }, []);

  const register = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const auth = getFailoverAwareAuth();
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const auth = getFailoverAwareAuth();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const auth = getFailoverAwareAuth();
      await signOut(auth);
    } catch (err: any) {
      setError(err.message || 'Logout failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, error, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  // Removed excessive debug logging - was causing console spam on every render
  // Use React DevTools or add conditional logging if needed for debugging
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 